import { isDefined, isNotDefined } from '@sgme/fp';
import { Badge, HStack, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { useRecordPriceMutation } from '@/api/mifid';
import { BulkPriceTableRow } from '@/App/BulkTrade/BulkPriceTableRow.tsx';
import { useRemainingTime } from '@/App/tiles/shared/useRemainingTime.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { checkIfUserCanTrade, checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { bulkTradeApi } from '@/store/bulkTrade/api';
import { getExecuteBulkTradeRfsMutationCacheKey } from '@/store/bulkTrade/api/executeBulkTradeRfs.ts';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import type { BulkPricedCashNettedLeg, BulkPricedCashSplitLeg } from '@/store/bulkTrade/state.ts';
import { getNettedLegByDate } from '@/store/bulkTrade/utilities/margin.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice.ts';
import { useCurrencyPrecision } from '@/utils/currencies/useCurrencyPrecision.ts';
import type { TimeInSecond } from '@/utils/time.ts';

type BulkPriceTableProps = {
  tabId: string;
};
export const BulkPriceTable = (props: BulkPriceTableProps) => {
  const { tabId } = props;

  const dispatch = useAppDispatch();
  const { formatDate, formatTime } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const userContext = useUserContext();
  const canTrade = checkIfUserCanTrade(userContext);
  const userEmail = userContext.currentUser.login;

  const signalRConnectionId = useAppSelector(getSignalRConnectionId);

  const [recordPrice] = useRecordPriceMutation();

  const {
    status,
    isMarginVisible,
    currencyPair,
    currency,
    allNettedLegs,
    allSplitLegs,
    negociatedCurrencyIndex,
    negociatedCurrency,
    reverseCurrency,
    rfsId,
    rfsRemainingTime,
    rfsProgress,
    rfsStartDate,
    negociations,
    rates,
    quoteId,
    nettedLegByDate,
    tradeIds,
    quoteFrontTimestamp,
    quoteBackTimestamp,
  } = useAppSelector((state) => {
    const bulkTrade = selectBulkTrade(state, tabId);

    if (isNotDefined(bulkTrade) || bulkTrade.step === 'building') {
      return {
        status: 'pending' as const,
        isMarginVisible: false,
        allNettedLegs: [] as BulkPricedCashNettedLeg[],
        allSplitLegs: [] as BulkPricedCashSplitLeg[],
        negociatedCurrencyIndex: 1 as const,
        negociatedCurrency: '',
        reverseCurrency: '',
        rates: [] as { bid: number; ask: number }[],
        nettedLegByDate: {},
      };
    }

    return {
      status: bulkTrade.step === 'priced' ? bulkTrade.step : bulkTrade.status,
      isMarginVisible: true,
      currencyPair: bulkTrade.currencyPair,
      currency: bulkTrade.negotiatedCurrencyIndex === 1 ? bulkTrade.currency2 : bulkTrade.currency1,
      allNettedLegs: bulkTrade.nettedLegs.toSorted((leftLeg, rightLeg) =>
        leftLeg.quoteLeg.Date.localeCompare(rightLeg.quoteLeg.Date),
      ),
      allSplitLegs: bulkTrade.splitLegs,
      negociatedCurrencyIndex: bulkTrade.negotiatedCurrencyIndex,
      negociatedCurrency: bulkTrade.negotiatedCurrencyIndex === 1 ? bulkTrade.currency1 : bulkTrade.currency2,
      reverseCurrency: bulkTrade.negotiatedCurrencyIndex === 1 ? bulkTrade.currency2 : bulkTrade.currency1,
      rfsId: bulkTrade.quote.RfsId,
      rfsRemainingTime: bulkTrade.step === 'priced' ? bulkTrade.rfsRemainingTime : 0,
      rfsProgress: bulkTrade.step === 'priced' ? (100.0 * bulkTrade.rfsRemainingTime) / bulkTrade.rfsWindow : 0,
      rfsStartDate: bulkTrade.rfsRequestedAt,
      negociations: bulkTrade.negociations.toSorted().reverse(),
      rates: bulkTrade.quote.Legs.map((leg) => ({
        ask: Number(leg.SpotWithoutMargin?.Ask ?? 0),
        bid: Number(leg.SpotWithoutMargin?.Bid ?? 0),
      })),
      quoteId: bulkTrade.quote.QuoteId,
      nettedLegByDate: getNettedLegByDate(bulkTrade.nettedLegs),
      tradeIds: bulkTrade.step === 'executed' && bulkTrade.status !== 'rejected' ? bulkTrade.tradeIds : undefined,
      quoteFrontTimestamp: bulkTrade.quoteFrontTimestamp,
      quoteBackTimestamp: bulkTrade.quoteBackTimestamp,
    };
  }, deepEqual);

  const remainingTime = useRemainingTime(rfsRemainingTime) ?? 0;

  const precision = useCurrencyPrecision(currencyPair);
  const isUserInternalSales = checkIfUserInternalSales(userContext);

  const handleCancelRfs = () => {
    if (isNotDefined(rfsId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'cancel RSF');

    dispatch(
      bulkTradeApi.endpoints.cancelBulkTradeRfs.initiate({
        tabId,
        rfsId,
      }),
    );
  };

  const handleExecuteBulkTrade = () => {
    if (isNotDefined(rfsId) || isNotDefined(signalRConnectionId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'execute RSF');

    dispatch(
      bulkTradeApi.endpoints.executeBulkTradeRfs.initiate(
        {
          rfsId,
          tabId,
          signalRConnectionId,
          precision,
          userEmail,
          isUserInternalSales,
          // TODO: remove fallback to Date.now()
          lastQuoteReceivedByFrontTimestamp: quoteFrontTimestamp ?? Date.now(),
          lastQuoteFromBackTimestamp: quoteBackTimestamp ?? Date.now(),
        },
        {
          fixedCacheKey: getExecuteBulkTradeRfsMutationCacheKey(tabId),
        },
      ),
    );
  };

  const handleRecordRates = useCallback(() => {
    if (isNotDefined(rfsId) || isNotDefined(rates) || isNotDefined(quoteId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('BulkTrade', 'RFS price recorded');

    recordPrice({
      tileId: tabId,
      date: Date.now(),

      quoteId,
      rfsId,
      prices: rates,
    });
  }, [tabId, rfsId, rates, quoteId, recordPrice, sgwtWebAnalytics]);

  const handleCloseExecutedBulkTrade = () => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'close executed bulk trade');

    dispatch(bulkTradeSlice.actions.done({ tabId }));
  };

  return (
    <>
      <table
        className="bulk table table-sm table-bordered table-thead-bordered table-body-bordered w-100 mb-0 bg-lvl1"
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          <col style={{ width: '170px' }} />
          <col style={{ width: '210px' }} />
          <col />
          <col />
          <col />
          <col />
          <col />

          {isMarginVisible && (
            <>
              <col />
              <col />
            </>
          )}
        </colgroup>

        <thead>
          <tr className="align-middle">
            <th>
              <FormattedMessage id="bulkTrade.table.header.product" />
            </th>
            <th>
              <FormattedMessage id="bulkTrade.table.header.way" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.amount" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.date" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.spot" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.points" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.allIn" />
            </th>

            {isMarginVisible && (
              <>
                <th className="text-end">
                  <FormattedMessage id="bulkTrade.table.header.spotMargin" />
                </th>
                <th className="text-end">
                  <FormattedMessage id="bulkTrade.table.header.margin" values={{ currency }} />
                </th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {allNettedLegs.map((leg) => (
            <BulkPriceTableRow
              key={leg.id}
              tabId={tabId}
              isEditable={status === 'priced'}
              rowType="netted"
              leg={leg}
              precision={precision}
              isMarginVisible={isMarginVisible}
              negociatedCurrencyIndex={negociatedCurrencyIndex}
              negociatedCurrency={negociatedCurrency}
              reverseCurrency={reverseCurrency}
              status={status}
            />
          ))}
        </tbody>
      </table>

      <p>
        <FormattedMessage id="bulkTrade.bookMessage" />
      </p>

      <table
        className="bulk table table-sm table-bordered table-thead-bordered table-body-bordered w-100 mb-0 bg-lvl1"
        style={{ tableLayout: 'fixed' }}
      >
        <colgroup>
          <col />
          <col />
          <col style={{ width: '170px' }} />
          <col style={{ width: '210px' }} />
          <col />
          <col />
          <col />
          <col />
          <col />

          {isMarginVisible && (
            <>
              <col />
              <col />
            </>
          )}
        </colgroup>

        <thead>
          <tr className="align-middle">
            <th>
              <FormattedMessage id="bulkTrade.table.header.status" />
            </th>

            <th>
              <FormattedMessage id="bulkTrade.table.header.tradeId" />
            </th>

            <th>
              <FormattedMessage id="bulkTrade.table.header.product" />
            </th>

            <th>
              <FormattedMessage id="bulkTrade.table.header.way" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.amount" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.date" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.spot" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.points" />
            </th>
            <th className="text-end">
              <FormattedMessage id="bulkTrade.table.header.allIn" />
            </th>

            {isMarginVisible && (
              <>
                <th className="text-end">
                  <FormattedMessage id="bulkTrade.table.header.spotMargin" />
                </th>
                <th align="right">
                  <FormattedMessage id="bulkTrade.table.header.margin" values={{ currency }} />
                </th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {allSplitLegs.map((leg, legIndex) => (
            <BulkPriceTableRow
              key={leg.id}
              tabId={tabId}
              isEditable={status === 'priced'}
              rowType="splitted"
              leg={leg}
              tradeId={tradeIds?.[legIndex]}
              nettedLegByDate={nettedLegByDate}
              precision={precision}
              isMarginVisible={isMarginVisible}
              negociatedCurrencyIndex={negociatedCurrencyIndex}
              negociatedCurrency={negociatedCurrency}
              reverseCurrency={reverseCurrency}
              status={status}
            />
          ))}
        </tbody>
      </table>

      {status === 'priced' && (
        <HStack horizontalAlignment="end" verticalAlignment="center" gap="8px">
          <div className="btn-group">
            <SgButton
              purpose="default"
              size="lg"
              icon="timer"
              iconPosition="start"
              iconOutlined
              onClick={handleRecordRates}
            >
              <FormattedMessage id="bulkTrade.buttons.timestamp" />
            </SgButton>

            {isDefined(rfsStartDate) && isDefined(negociations) && (
              <OverlayTrigger
                overlay={
                  <Popover style={{ maxWidth: '300px' }}>
                    <Popover.Header data-e2e="timestamp-popover-title">
                      <strong>
                        <FormattedMessage id="cash.forward.negociation.popover.header" values={{ currencyPair }} />
                      </strong>
                    </Popover.Header>

                    <Popover.Body>
                      <VStack className="mb-8px">
                        <div className="mb-8px">
                          <FormattedMessage
                            id="cash.forward.negociation.popover.initialisation"
                            values={{
                              initialisationDate: `${formatDate(rfsStartDate, { dateStyle: 'long' })} ${formatTime(rfsStartDate, { timeStyle: 'short' })}`,
                            }}
                          />
                        </div>

                        <div data-e2e="timestamp-popover-body">
                          {negociations.map((negociation, negociationIndex) => (
                            // oxlint-disable-next-line no-array-index-key -- the index is really the key
                            <div key={negociationIndex} className="text-nowrap">
                              <FormattedMessage
                                id="cash.forward.negociation.popover.item"
                                values={{
                                  index: negociations.length - negociationIndex,
                                  date: `${formatDate(negociation, { dateStyle: 'medium' })} ${formatTime(negociation, { timeStyle: 'short' })}`,
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </VStack>
                    </Popover.Body>
                  </Popover>
                }
                placement="top"
                trigger={['hover', 'focus']}
              >
                <SgButton purpose="default" size="lg" onClick={handleRecordRates} className="px-2">
                  <Badge purpose="primary" isPill>
                    {negociations?.length ?? 0}
                  </Badge>
                </SgButton>
              </OverlayTrigger>
            )}
          </div>

          <SgButton purpose="outline" size="lg" onClick={handleCancelRfs} data-e2e="cancel-button">
            <FormattedMessage id="bulkTrade.buttons.cancel" />
          </SgButton>

          <SgButton
            purpose="primary"
            color="primary"
            size="lg"
            onClick={handleExecuteBulkTrade}
            progress={rfsProgress}
            disabled={!canTrade}
            data-e2e="book-button"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            <FormattedMessage
              id="bulkTrade.buttons.placeTrades"
              values={{
                tradeCount: allNettedLegs.length,
                remainingTime: formatRemaingTime(remainingTime),
              }}
            />
          </SgButton>
        </HStack>
      )}

      {status !== 'priced' && (
        <HStack horizontalAlignment="end" verticalAlignment="center" gap="8px">
          <SgButton purpose="default" onClick={handleCloseExecutedBulkTrade} data-e2e="done-button">
            <FormattedMessage id="bulkTrade.buttons.done" />
          </SgButton>
        </HStack>
      )}
    </>
  );
};

function formatRemaingTime(time: TimeInSecond) {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
